import type {
  AITestAggregateDto,
  AITestDto,
  ChapterDto,
  CheckItemDto,
  CreateChapterInput,
  CreateCheckItemInput,
  CreateEventInput,
  CreateMindMapInput,
  CreateNodeInput,
  CreateNodeRelationshipInput,
  CreateSubjectInput,
  EventDto,
  GenerateAITestInput,
  GrantCreditTopUpInput,
  SubmitTranslationReviewInput,
  SubmitTranslationSuggestionInput,
  SetTranslationCampaignInput,
  DisableTranslationOverrideInput,
  MarkFreeTextResponseInput,
  MindMapAggregateDto,
  MindMapDto,
  MindMapWritingDto,
  NodeDto,
  NodeRelationshipDto,
  QuestionInput,
  ReorderAITestsInput,
  ReorderChaptersInput,
  ReorderCheckItemsInput,
  SaveQuestionResponseInput,
  SubjectDto,
  SubjectOverviewDto,
  TestAttemptAggregateDto,
  TestAttemptDto,
  UpdateAITestInput,
  UpdateCheckItemInput,
  UpdateEventInput,
  UpdateNodeInput,
  UpdateMindMapWritingInput,
  UpdateNodeRelationshipInput,
  UpdateSubjectInput,
  UpdateChapterInput,
  AcceptConsentInput,
  ConsentDto,
  UpdatePreferencesInput,
  UserPreferencesDto,
  WritingDto,
} from "@mmm/contracts";
import { ApiRequestError } from "@mmm/contracts";
import { AuthRequiredError, postCommand } from "./client.js";
import { invalidate } from "./invalidate.js";
import { trackSave } from "./saveStatus.js";
import { pushToast } from "../components/shell/ToastProvider.js";
import { triggerSignIn } from "../auth/AuthProvider.js";
import type { ToastAction } from "../components/molecules/Toast.js";
import { type MessageKey, t } from "@mmm/ui";
// Message key for the short human verb shown as the error toast's title,
// keyed by command name. Anything not listed here falls back to a generic
// "Saving changes". Translated lazily in errorToast() — never at module load.
const COMMAND_LABEL_KEYS: Record<string, MessageKey> = {
  createSubject: "subjects.creatingSubject",
  updateSubject: "subjects.savingSubject",
  archiveSubject: "subjects.archivingSubject",
  activateSubject: "subjects.restoringSubject",
  deleteSubject: "subjects.deletingSubject",
  restoreSubject: "subjects.restoringSubject",
  createChapter: "chapters.creatingChapter",
  updateChapter: "chapters.savingChapter",
  archiveChapter: "chapters.archivingChapter",
  activateChapter: "chapters.restoringChapter",
  reorderChapters: "chapters.reorderingChapters",
  deleteChapter: "chapters.deletingChapter",
  restoreChapter: "chapters.restoringChapter",
  createCheckItem: "tasks.addingTask",
  updateCheckItem: "tasks.savingTask",
  reorderCheckItems: "tasks.reorderingTasks",
  deleteCheckItem: "tasks.deletingTask",
  createEvent: "events.addingEvent",
  updateEvent: "events.savingEvent",
  deleteEvent: "events.deletingEvent",
  createMindMap: "maps.creatingMap",
  createNode: "maps.addingNode",
  updateNode: "maps.savingNode",
  updateWriting: "maps.savingYourNotes",
  updateMindMapWriting: "maps.savingYourNotes",
  deleteNode: "maps.deletingNode",
  createNodeRelationship: "maps.connectingNodes",
  updateNodeRelationship: "maps.savingConnection",
  deleteNodeRelationship: "maps.deletingConnection",
  generateAITest: "tests.generatingTest",
  updateAITest: "tests.savingTest",
  replaceAITestQuestions: "tests.savingQuestions",
  reorderAITests: "tests.reorderingTests",
  deleteAITest: "tests.deletingTest",
  startTestAttempt: "tests.startingAttempt",
  saveQuestionResponse: "tests.savingYourAnswer",
  discardTestAttempt: "tests.discardingAttempt",
  submitTestAttempt: "tests.submittingTest",
  markFreeTextResponse: "tests.savingMark",
  grantCreditTopUp: "credits.addCredits",
  submitTranslationSuggestion: "translations.suggestCorrection",
  submitTranslationReview: "translations.submitReview",
  setTranslationCampaign: "translations.manageCampaign",
  disableTranslationOverride: "translations.disableOverride",
};

/** Marks an error as already surfaced to the user, so other layers (the
 * global `unhandledrejection` backstop in main.tsx) don't toast it again. */
function markReported(err: unknown): void {
  if (err && typeof err === "object") {
    Object.defineProperty(err, "reported", { value: true, enumerable: false, configurable: true });
  }
}

function errorToast(err: unknown, commandName: string): { title: string; message: string; action?: ToastAction } {
  const title = t(COMMAND_LABEL_KEYS[commandName] ?? "common.savingChanges");

  if (err instanceof AuthRequiredError) {
    return {
      title: t("errors.sessionExpired"),
      message: t("errors.signInAgainToKeepSaving"),
      action: { label: t("auth.signIn"), onClick: triggerSignIn },
    };
  }

  if (err instanceof ApiRequestError) {
    if (err.code === "insufficientCredits") return { title, message: t("credits.insufficient") };
    if (err.code === "rateLimited") {
      return { title, message: t("errors.rateLimited") };
    }
    // err.message and fieldErrors[].message are server-authored by design
    // (error localization would map by code) — passed through untranslated.
    const fieldSummary = err.fieldErrors?.length ? ` ${err.fieldErrors.map((field) => field.message).join(" ")}` : "";
    return { title, message: `${err.message}${fieldSummary}` };
  }

  // Anything else — ApiTransportError (bad/invalid HTTP response) or a raw
  // fetch() rejection (the browser throws a plain TypeError when the network
  // is unreachable, never reaching ApiTransportError) — means the request
  // never got a valid response from the API.
  return { title, message: t("errors.serverUnreachable") };
}

/**
 * Runs one command mutation: tracks the save-status indicator, invalidates
 * cached queries on success, and on failure pushes an error toast and
 * re-throws (mandatory — callers like test submission `await` this and must
 * still see the failure; silently swallowing would hide a lost write).
 *
 * Double-reporting: three dialogs (SubjectDialog, GenerateTestDialog,
 * AITestEditorScreen) already catch ApiRequestError and render their own
 * inline message. This intentionally still toasts for those too — the toast
 * and the inline text are both brief and the user is already looking at the
 * dialog, so the small duplication there is preferable to threading a
 * suppression flag through call sites that predate this system. The
 * `reported` marker below exists for the one case where duplication *would*
 * be confusing: an error that's re-thrown out of a dialog handler and left
 * uncaught becomes an unhandled promise rejection, which main.tsx's global
 * backstop would otherwise toast a second time.
 */
async function run<T>(commandName: string, input: unknown): Promise<T> {
  const releaseSave = trackSave();
  try {
    const data = await postCommand<T>(commandName, input);
    invalidate();
    releaseSave(true);
    return data;
  } catch (err) {
    releaseSave(false);
    pushToast({ variant: "error", ...errorToast(err, commandName) });
    markReported(err);
    throw err;
  }
}

/**
 * Invocation convention (WP-10): every command call site must do one of two
 * things with the promise it gets back —
 *
 *  1. `await` it (inside a `try`/`catch` if the caller wants to react to the
 *     specific failure, e.g. show inline dialog text) — the normal case when
 *     a caller needs the result or must sequence after it settles.
 *  2. Wrap it in `fireCommand(...)` for genuine fire-and-forget invocations
 *     (an event handler that doesn't need to await the result, e.g. toggling
 *     a checkbox or dragging a node) — this is the ONLY sanctioned way to
 *     leave a command's promise un-awaited. Bare `void command(...)` or a
 *     naked `command(...)` statement are both banned: `run()` above already
 *     toasts and re-throws on failure, so an un-awaited, uncaught call site
 *     becomes a genuine unhandled promise rejection (a console warning even
 *     though the user already saw a toast) instead of a silently-handled one.
 *
 * `fireCommand` swallows the (already-reported) rejection so it never
 * reaches the browser as "Uncaught (in promise)". Do NOT use it to hide
 * errors a caller actually needs to know about — it is for optimistic,
 * no-result-needed mutations only.
 */
export function fireCommand(promise: Promise<unknown>): void {
  promise.catch(() => {
    // run() above already toasted the user and marked the error `reported`
    // — this catch exists purely to keep the rejection from also surfacing
    // as a console-level "Uncaught (in promise)" warning. Nothing left to do.
  });
}

// ---- Subjects ----
export const createSubject = (input: CreateSubjectInput) => run<SubjectDto>("createSubject", input);
export const updateSubject = (input: UpdateSubjectInput) => run<SubjectDto>("updateSubject", input);
export const archiveSubject = (input: { subjectId: string }) => run<SubjectDto>("archiveSubject", input);
export const activateSubject = (input: { subjectId: string }) => run<SubjectDto>("activateSubject", input);
export const deleteSubject = (input: { subjectId: string }) => run<{ subjectId: string }>("deleteSubject", input);
export const restoreSubject = (input: { subjectId: string }) => run<SubjectDto>("restoreSubject", input);

// ---- Chapters ----
export const createChapter = (input: CreateChapterInput) => run<SubjectOverviewDto>("createChapter", input);
export const updateChapter = (input: UpdateChapterInput) => run<ChapterDto>("updateChapter", input);
export const archiveChapter = (input: { chapterId: string }) => run<ChapterDto>("archiveChapter", input);
export const activateChapter = (input: { chapterId: string }) => run<ChapterDto>("activateChapter", input);
export const reorderChapters = (input: ReorderChaptersInput) => run<ChapterDto[]>("reorderChapters", input);
export const deleteChapter = (input: { chapterId: string }) => run<SubjectOverviewDto>("deleteChapter", input);
export const restoreChapter = (input: { chapterId: string }) => run<ChapterDto>("restoreChapter", input);

// ---- Check items ----
export const createCheckItem = (input: CreateCheckItemInput) => run<CheckItemDto>("createCheckItem", input);
export const updateCheckItem = (input: UpdateCheckItemInput) => run<CheckItemDto>("updateCheckItem", input);
export const reorderCheckItems = (input: ReorderCheckItemsInput) => run<CheckItemDto[]>("reorderCheckItems", input);
export const deleteCheckItem = (input: { checkItemId: string }) => run<{ checkItemId: string }>("deleteCheckItem", input);

// ---- Events ----
export const createEvent = (input: CreateEventInput) => run<EventDto>("createEvent", input);
export const updateEvent = (input: UpdateEventInput) => run<EventDto>("updateEvent", input);
export const deleteEvent = (input: { eventId: string }) => run<{ eventId: string }>("deleteEvent", input);

// ---- Mind maps ----
// One map per subject / per chapter — createMindMap only lazily creates that
// single map (server rejects duplicates); maps live and die with their scope.
export const createMindMap = (input: CreateMindMapInput) => run<MindMapDto>("createMindMap", input);

// ---- Nodes / writings / relationships ----
export const createNode = (input: CreateNodeInput) => run<MindMapAggregateDto>("createNode", input);
export const updateNode = (input: UpdateNodeInput) => run<NodeDto>("updateNode", input);
export const updateWriting = (input: { nodeId: string; markdown: string }) => run<WritingDto>("updateWriting", input);
export const updateMindMapWriting = (input: UpdateMindMapWritingInput) =>
  run<MindMapWritingDto>("updateMindMapWriting", input);
export const deleteNode = (input: { nodeId: string }) => run<MindMapAggregateDto>("deleteNode", input);
export const createNodeRelationship = (input: CreateNodeRelationshipInput) =>
  run<MindMapAggregateDto>("createNodeRelationship", input);
export const updateNodeRelationship = (input: UpdateNodeRelationshipInput) =>
  run<NodeRelationshipDto>("updateNodeRelationship", input);
export const deleteNodeRelationship = (input: { relationshipId: string }) =>
  run<{ relationshipId: string }>("deleteNodeRelationship", input);

// ---- AI tests ----
export const generateAITest = (input: GenerateAITestInput) => run<AITestAggregateDto>("generateAITest", input);
export const updateAITest = (input: UpdateAITestInput) => run<AITestDto>("updateAITest", input);
export const replaceAITestQuestions = (input: { testId: string; questions: QuestionInput[] }) =>
  run<AITestAggregateDto>("replaceAITestQuestions", input);
export const reorderAITests = (input: ReorderAITestsInput) => run<AITestDto[]>("reorderAITests", input);
export const deleteAITest = (input: { testId: string }) => run<{ testId: string }>("deleteAITest", input);

// ---- Attempts ----
export const startTestAttempt = (input: { testId: string }) => run<TestAttemptAggregateDto>("startTestAttempt", input);
export const saveQuestionResponse = (input: SaveQuestionResponseInput) =>
  run<TestAttemptAggregateDto>("saveQuestionResponse", input);
export const discardTestAttempt = (input: { attemptId: string }) => run<TestAttemptDto>("discardTestAttempt", input);
export const submitTestAttempt = (input: { attemptId: string }) => run<TestAttemptAggregateDto>("submitTestAttempt", input);
export const markFreeTextResponse = (input: MarkFreeTextResponseInput) =>
  run<TestAttemptAggregateDto>("markFreeTextResponse", input);
export const grantCreditTopUp = (input: GrantCreditTopUpInput) =>
  run<{ userId: string; amount: number }>("grantCreditTopUp", input);
export const submitTranslationSuggestion = (input: SubmitTranslationSuggestionInput) =>
  run<{ id: string; status: string }>("submitTranslationSuggestion", input);
export const submitTranslationReview = (input: SubmitTranslationReviewInput) =>
  run<{ status: string; outcome?: string }>("submitTranslationReview", input);
export const setTranslationCampaign = (input: SetTranslationCampaignInput) =>
  run<{ enabled: boolean }>("setTranslationCampaign", input);
export const disableTranslationOverride = (input: DisableTranslationOverrideInput) =>
  run<{ disabled: boolean }>("disableTranslationOverride", input);

// ---- User settings ----
export const updatePreferences = (input: UpdatePreferencesInput) =>
  run<UserPreferencesDto>("updatePreferences", input);

// ---- Consent ----
export const acceptConsent = (input: AcceptConsentInput) => run<ConsentDto>("acceptConsent", input);
